'use-client';
import { ChangeEvent, RefObject, useCallback, useReducer, useRef, useState } from 'react';
import {
  Stack,
  Popper,
  InputAdornment,
  InputBase,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { Animation } from '../layout/header/Animation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';
import { ROUTE_FILTERS, useRouter } from '@/navigation';
import {
  QuickSearchResultBlockHandle,
  QuickSearchResultBlock,
  QuickSearchResultBlockProps,
  QuickSearchProduct,
} from './QuickSearchResultBlock';
import { useTranslations } from 'next-intl';
import { theme } from '../theme';
import { QuickSearchNoResultBlock } from './QuickSearchNoResultBlock';
import { useEtilizeSearch } from '../hooks';
import { useCompetitorSearch } from '../hooks/useCompetitorSearch';
import { useDisplaySearch } from '../hooks/useDisplaySearch';

type SearchConfig<TProduct extends QuickSearchProduct = QuickSearchProduct> = {
  queryKey: string;
  titleKey: string;
} & Pick<
  QuickSearchResultBlockProps<TProduct>,
  | 'useSearch'
  | 'getProductUrl'
  | 'getAllResultsUrl'
  | 'getProductTitle'
  | 'ProductDescriptionComponent'
  | 'FooterComponent'
  | 'hasProductImages'
>;

function createSearchConfig<TProduct extends QuickSearchProduct = QuickSearchProduct>(
  config: SearchConfig<TProduct>
): SearchConfig<TProduct> {
  return config;
}

/**
 * Search configurations for the QuickSearchWidget.
 * ----
 * @param queryKey - The key for the search configuration used in the search state and the query provider.
 * @param titleKey - The translation key for the title of the search block.
 * @param useSearch - The search hook to use for the search block.
 * @param getProductUrl - The function to get the product URL for a given SKU.
 * @param getAllResultsUrl - The function to get the URL for all results for a given keyword, or undefined to disable.
 */
const searchConfigs = [
  createSearchConfig({
    queryKey: 'compatibility-search',
    titleKey: 'compatibility',
    useSearch: useDisplaySearch,
    getProductUrl: ({ sku }) =>
      `/collections/mounting-solutions?keywords=${encodeURIComponent(sku)}&filter=${ROUTE_FILTERS.COMPATIBILITY}`,
    ProductDescriptionComponent: () => {
      return null;
    },
    FooterComponent: ({ count }) => {
      const t = useTranslations('Search');
      if (count > 1) {
        return (
          <Typography component="span" variant="body2" sx={{ color: 'gray.dark' }}>
            {t('compatible-multiple-results')}
          </Typography>
        );
      }

      return null;
    },
    hasProductImages: false,
  }),
  createSearchConfig({
    queryKey: 'mount-search',
    titleKey: 'mounting-solutions',
    useSearch: useEtilizeSearch.bind(null, { category: 'mounts' }),
    getAllResultsUrl: keyword =>
      `/collections/mounting-solutions?keywords=${encodeURIComponent(keyword)}`,
  }),
  createSearchConfig({
    queryKey: 'kiosk-search',
    titleKey: 'kiosk-solutions',
    useSearch: useEtilizeSearch.bind(null, { category: 'kiosks' }),
    getAllResultsUrl: keyword =>
      `/collections/kiosk-solutions?keywords=${encodeURIComponent(keyword)}`,
  }),
  createSearchConfig({
    queryKey: 'display-search',
    titleKey: 'display-solutions',
    useSearch: useEtilizeSearch.bind(null, { category: 'displays' }),
    getAllResultsUrl: keyword =>
      `/collections/display-solutions?keywords=${encodeURIComponent(keyword)}`,
  }),
  createSearchConfig({
    queryKey: 'other-search',
    titleKey: 'other',
    useSearch: useEtilizeSearch.bind(null, { category: 'other' }),
    getAllResultsUrl: keyword => `/collections/other?keywords=${encodeURIComponent(keyword)}`,
  }),
  createSearchConfig({
    queryKey: 'competitor-search',
    titleKey: 'competitor',
    useSearch: useCompetitorSearch,
    getProductUrl: product => `/products/${product.sku}?filter=${ROUTE_FILTERS.COMPETITOR}`,
    ProductDescriptionComponent: product => {
      const t = useTranslations('Search');
      return (
        <Typography component="span" variant="body2" sx={{ color: 'gray.dark' }}>
          {t('competitor-alternative', { text: product.text, sku: product.sku })}
        </Typography>
      );
    },
  }),
];

interface QuickSearchWidgetProps {
  inputRef?: RefObject<HTMLInputElement>;
  onClose?(): void;
}

export const QuickSearchWidget: React.FC<QuickSearchWidgetProps> = ({
  inputRef,
  onClose,
}: QuickSearchWidgetProps) => {
  const t = useTranslations('Search');
  const router = useRouter();

  const searchInputAnchorRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [{ noResults, interacting, isLoading }, dispatch] = useReducer(searchStateReducer, {
    noResults: true,
    interacting: true,
    isLoading: false,
    instances: {},
  });

  const [keyword, setKeyword] = useState<string>('');

  const keywordTrimmed = keyword.trim();
  const showNoResultBlock = !!keywordTrimmed && !isLoading && !interacting && noResults;

  const handleSetAnchorEl = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setKeyword(e.target.value);
    },
    [setKeyword]
  );

  const handleReset = useCallback(() => {
    setKeyword('');
    dispatch({ type: 'reset' });
  }, [setKeyword, dispatch]);

  const handleSelect = useCallback(
    (url: string) => {
      onClose?.();
      handleReset();
      router.push(url);
    },
    [onClose, handleReset, router]
  );

  const handleStatusChange = useCallback(
    (key: string, isLoading: boolean) => {
      dispatch({ type: 'loading', key, isLoading });
    },
    [dispatch]
  );

  const handleInteracting = useCallback(
    (key: string, active: boolean) => {
      dispatch({ type: 'interacting', key, active });
    },
    [dispatch]
  );

  const handleResultChange = useCallback(
    (key: string, status: boolean) => {
      dispatch({ type: 'result', key, count: status ? 1 : 0 });
    },
    [dispatch]
  );

  const instanceRef = useCallback(
    (ref: QuickSearchResultBlockHandle) => {
      ref?.key && dispatch({ type: 'mount', key: ref.key, ref });
    },
    [dispatch]
  );

  return (
    <Stack>
      <InputBase
        placeholder={t('search-placeholder')}
        ref={searchInputAnchorRef}
        inputRef={inputRef}
        value={keyword}
        fullWidth
        inputProps={{ sx: { padding: 0 } }}
        color="info"
        sx={theme => ({
          border: `4px solid`,
          borderColor: 'gray.light',
          padding: 2,
          backgroundColor: 'transparent.white',
          fontSize: 'large',
          fontWeight: '700',
          transition: 'border-color 0.5s ease, background-color 0.5s ease',
          '&:focus-within': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.common.white,
          },
        })}
        startAdornment={
          <InputAdornment position="start">
            <FontAwesomeIcon color={theme.palette.common.black} icon={icons.magnify} />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
            <FontAwesomeIcon icon={icons.close} onClick={handleReset} />
          </InputAdornment>
        }
        onChange={handleInputChange}
        onFocus={handleSetAnchorEl}
      />
      <LinearProgress
        sx={{
          height: '4px',
          marginTop: '-4px',
          display: isLoading ? 'block' : 'none',
          backgroundColor: theme.palette.primary.light,
        }}
      />
      <Popper
        anchorEl={anchorEl}
        open={true}
        placement="bottom"
        transition
        keepMounted
        sx={{ width: anchorEl?.offsetWidth, zIndex: 2 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 1],
            },
          },
          { name: 'flip', enabled: false },
        ]}
      >
        {({ TransitionProps }) => (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TransitionProps is always defined based on transition prop above
          <Animation {...TransitionProps!}>
            <Paper
              elevation={4}
              sx={{
                maxHeight: '50vh',
                overflow: 'auto',
                marginTop: '20px',
              }}
            >
              {searchConfigs.map(({ queryKey, titleKey, ...config }) => (
                <QuickSearchResultBlock
                  key={queryKey}
                  queryKey={queryKey}
                  ref={instanceRef}
                  {...config}
                  title={t(titleKey as never)}
                  keyword={keywordTrimmed}
                  onNavigate={handleSelect}
                  onStatusChange={handleStatusChange}
                  onInteracting={handleInteracting}
                  onResultChange={handleResultChange}
                />
              ))}
            </Paper>
            {showNoResultBlock && <QuickSearchNoResultBlock keyword={keywordTrimmed} />}
          </Animation>
        )}
      </Popper>
    </Stack>
  );
};

/**
 * Instance of the QuickSearchResultBlock component that ties into the widget search state.
 */
interface SearchInstance {
  ref: QuickSearchResultBlockHandle | null;
  count: number;
  isLoading: boolean;
  interacting: boolean;
}

/**
 * Search state for the QuickSearchWidget component and its children QuickSearchResultBlocks.
 */
interface SearchState {
  noResults: boolean;
  interacting: boolean;
  isLoading: boolean;

  instances: {
    [key: string]: SearchInstance;
  };
}

/**
 * State actions for the QuickSearchWidget.
 */
type SearchInstanceAction =
  | { type: 'mount'; key: string; ref: QuickSearchResultBlockHandle }
  | { type: 'result'; key: string; count: number }
  | { type: 'interacting'; key: string; active: boolean }
  | { type: 'loading'; key: string; isLoading: boolean }
  | { type: 'reset' };

function searchStateReducer(state: SearchState, action: SearchInstanceAction): SearchState {
  function update(key: string, updater: (instance: SearchInstance) => Partial<SearchInstance>) {
    return {
      ...state,
      instances: {
        ...state.instances,
        [key]: {
          ...state.instances[key],
          ...updater(state.instances[key]),
        },
      },
    };
  }

  let newState = state;

  switch (action.type) {
    case 'mount':
      newState = update(action.key, () => ({
        ref: action.ref,
      }));
      break;

    case 'result':
      newState = update(action.key, () => ({
        count: action.count,
      }));
      newState.noResults = Object.values(newState.instances).every(
        instance => instance.count === 0
      );
      break;

    case 'interacting':
      newState = update(action.key, () => ({
        interacting: action.active,
      }));
      newState.interacting = Object.values(newState.instances).some(
        instance => instance.interacting
      );
      break;

    case 'loading':
      newState = update(action.key, () => ({
        isLoading: action.isLoading,
      }));
      newState.isLoading = Object.values(newState.instances).some(instance => instance.isLoading);
      break;

    case 'reset':
      Object.values(state.instances).forEach(instance => instance.ref?.reset());
      break;
  }

  return newState;
}
